import clsx from "clsx";
import { forwardRef, ReactNode } from "react";
import { useForwardedOrLocalRef, useUniqueIds } from "../../../shared/hooks";
import { ClearButton } from "./components";
import styles from "./DefaultTextField.module.css";
import type { BaseProps } from "./shared";

export const Sizes = ["small", "medium", "large"] as const;
export const Variant = "default";

export interface Props extends BaseProps {
  /**
   * Docs:
   * - {@link https://www.figma.com/design/erhFTAWNMugPVrJGCAWsse/%E2%9D%96-Dojo-Design-System-%E2%80%93-DDS?node-id=781-11468 Figma}
   * - {@link https://components.internal.classdojo.com/?path=/story/components-ddstextfield--default-variant Storybook}
   */
  variant?: typeof Variant;
  ariaLabel?: string;
  size?: (typeof Sizes)[number];
  /** If labelSize is provided, it will override the size prop for the label only. */
  labelSize?: (typeof Sizes)[number];
  placeholder?: string;
  autoFocus?: boolean;
  leftSlot?: ReactNode;
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>["autoComplete"];
}

export const DefaultTextField = forwardRef<HTMLInputElement, Props>(
  (
    {
      ["data-name"]: dataName,
      disabled,
      id,
      label,
      ariaLabel,
      size = "medium",
      labelSize = size,
      error,
      hint,
      placeholder,
      clearButton,
      rightSlot,
      leftSlot,
      onChange,
      "aria-describedby": ariaDescribedByFromUser,
      ...rest
    },
    forwardedRef,
  ) => {
    const ref = useForwardedOrLocalRef(forwardedRef);
    const [labelId, inputId, hintId] = useUniqueIds(["DDSTextField.label", "DDSTextField.input", "DDSTextField.hint"]);
    const ariaDescribedByMerged =
      [hint ? hintId : undefined, ariaDescribedByFromUser].filter(Boolean).join(" ").trim() || undefined;

    const renderRightSlot = () => {
      if (clearButton && rightSlot) {
        return (
          <>
            <div className={styles.rightSlot}>{rightSlot}</div>
            <ClearButton className={styles.clearButton} forwardedRef={ref} />
          </>
        );
      }

      if (clearButton) {
        return <ClearButton className={styles.clearButton} forwardedRef={ref} />;
      }

      if (rightSlot) {
        return rightSlot;
      }

      return null;
    };

    return (
      <div className={clsx(styles.textField, styles[size], error && styles.error)}>
        <label id={labelId} htmlFor={id ?? inputId} className={clsx(styles.label, styles[labelSize])}>
          {label}
        </label>
        <div className={styles.inputBox}>
          {leftSlot && <div className={styles.leftSlot}>{leftSlot}</div>}
          <input
            aria-label={ariaLabel || undefined}
            className={styles.input}
            data-name={dataName}
            disabled={disabled}
            id={id ?? inputId}
            placeholder={placeholder ?? " "}
            ref={ref}
            aria-describedby={ariaDescribedByMerged}
            aria-invalid={error}
            onChange={(e) => {
              onChange?.(e.target.value, e);
            }}
            {...rest}
          />
          {renderRightSlot()}
        </div>
        {hint && (
          <span
            role={error ? "alert" : undefined}
            aria-live={!error ? "polite" : undefined}
            id={hintId}
            className={styles.hint}
          >
            {hint}
          </span>
        )}
      </div>
    );
  },
);
